import API from "@services/root/rootApi";
import {
  DeviceCatalog,
  DeviceModelDetail,
  CatalogFilters,
  DeviceAdapter,
  DeviceModelUpdate,
} from "./types";
import { Dispatch, SetStateAction } from "react";

export const getCatalog = async (
  activeOnly?: boolean,
  includeStatistics?: boolean, // only used by SYAMP
  maxResults?: number,
  orderBy?: string,
  startIndex?: number,
  filters?: CatalogFilters | undefined
): Promise<DeviceCatalog> => {
  const response = await API.post<DeviceCatalog>(
    `/symphony-api/catalog`,
    filters,
    {
      params: {
        activeOnly: activeOnly,
        maxResults: maxResults,
        includeStatistics: includeStatistics,
        orderBy: orderBy,
        startIndex: startIndex,
      },
    }
  );

  return response.data;
};

export const getModels = async (
  models?: Array<string>
): Promise<DeviceModelDetail[]> => {
  const params: any = {
    models: models,
  };

  const response = await API.get<DeviceModelDetail[]>(
    `/symphony-api/catalog/models`,
    {
      params,
    }
  );

  return response.data;
};

export const postModels = async (
  modelId: number,
  data: DeviceModelUpdate
): Promise<DeviceModelDetail[]> => {
  const response = await API.post<DeviceModelDetail[]>(
    `/symphony-api/catalog/models/${modelId}`,
    data
  );

  return response.data;
};
export const getModelData = async (
  model: string
): Promise<DeviceModelDetail> => {
  const response = await API.get<DeviceModelDetail>(
    `/symphony-api/catalog/model?model=${encodeURIComponent(model)}`
  );

  return response.data;
};

export const getModelDataWithAccountId = async (
  accountId: string,
  model: string
): Promise<DeviceModelDetail> => {
  const response = await API.get<DeviceModelDetail>(
    `/symphony-api/catalog/${accountId}/model?model=${encodeURIComponent(
      model
    )}`
  );

  return response.data;
};

export interface DeviceFieldValues {
  manufacturers: string[];
  categories: string[];
  deviceTypes: string[];
}
export const getDeviceFieldsFromModels = (
  models: DeviceModelDetail[]
): DeviceFieldValues => {
  const manufacturers: { [k in string]: any } = {};
  const categories: { [k in string]: any } = {};
  const deviceTypes: { [k in string]: any } = {};

  models.forEach((model) => {
    manufacturers[model.manufacturer as string] = true;
    categories[model.category as string] = true;
    deviceTypes[model.deviceType as string] = true;
  });
  return {
    manufacturers: Object.keys(manufacturers),
    categories: Object.keys(categories),
    deviceTypes: Object.keys(deviceTypes),
  };
};
export const getDeviceFields = async (): Promise<DeviceFieldValues> =>
  getDeviceFieldsFromModels(await getModels());

export const uploadCatalog = async (
  data: FormData,
  setProgress: Dispatch<SetStateAction<number>>
) => {
  const response = await API.post(`/symphony-api/catalog/import`, data, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
    onUploadProgress: (progressEvent) => {
      const progress = (progressEvent.progress ?? 0) * 100;
      setProgress(progress);
    },
  });
  return response;
};

export const getAccountDeviceCatalog = async (
  accountId: string,
  activeOnly?: boolean,
  includeStatistics?: boolean, // only used by SYAMP
  maxResults?: number,
  orderBy?: string,
  startIndex?: number,
  filters?: CatalogFilters
): Promise<DeviceCatalog> => {
  const response = await API.post<DeviceCatalog>(
    `/symphony-api/catalog/${accountId}`,
    filters,
    {
      params: {
        activeOnly: activeOnly,
        maxResults: maxResults,
        includeStatistics: includeStatistics,
        orderBy: orderBy,
        startIndex: startIndex,
      },
    }
  );

  return response.data;
};

export const getDeviceAdapters = async (
  deviceModel: DeviceModelDetail["model"],
  accountId?: string
): Promise<DeviceAdapter[]> => {
  const response = await API.post<DeviceAdapter[]>(
    `/symphony-api/provisioning/device/adapter`,
    {
      deviceModel: [deviceModel],
      accountId: accountId ? [accountId] : [],
    }
  );
  return response.data;
};

export const getAccountDeviceAdapters = async (
  accountId: string
): Promise<DeviceAdapter[]> => {
  const response = await API.post<DeviceAdapter[]>(
    `/symphony-api/provisioning/device/adapter`,
    {
      params: {
        accountid: [accountId],
      },
    }
  );
  return response.data;
};

export const updateAccountDeviceModel = async (
  accountId: string,
  deviceId: DeviceModelDetail["id"],
  data: DeviceModelUpdate
) => {
  const response = await API.post(
    `/symphony-api/catalog/${accountId}/models/${deviceId}`,
    data
  );

  return response;
};
